import { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Plus, X } from 'lucide-react';
import Button from '../ui/Button';
import pbrService, { type IpgodCandidate, type IpgodCandidateReview } from '../../services/pbr';
import { useOrgTimezone, formatLocalDateInTz } from '@/utils/timezone';

interface Props {
  cultivarId: number;
  cultivarName: string;
  candidates: IpgodCandidate[];
  /** The number on the cultivar's AU PBR protection, if it has one. */
  existingAuPbrNumber?: string | null;
  /** Called after a protection is added, so the page can reload the cultivar. */
  onApplied: () => void;
  onClose: () => void;
}

type RowState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'review'; data: IpgodCandidateReview }
  | { kind: 'adding'; data: IpgodCandidateReview }
  | { kind: 'added'; data: IpgodCandidateReview }
  | { kind: 'error'; message: string; data?: IpgodCandidateReview };

function errorMessage(e: unknown): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message || (e instanceof Error ? e.message : 'Request failed');
}

/**
 * The AU PBR registry sync only writes a record whose denomination is exactly
 * the cultivar's name or trade name. Anything close is listed here: Review
 * reads the IPGOD record in full, and Add writes it as the cultivar's AU PBR
 * protection. Nothing is written until Add is pressed.
 */
export default function IpgodCandidatesPanel({ cultivarId, cultivarName, candidates, existingAuPbrNumber, onApplied, onClose }: Props) {
  const orgTz = useOrgTimezone();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [open, setOpen] = useState<string | null>(null);

  const setRow = (appNo: string, s: RowState) => setRows((prev) => ({ ...prev, [appNo]: s }));
  const date = (d: string | null | undefined) => (d ? formatLocalDateInTz(d.slice(0, 10), orgTz) : '—');

  const review = async (appNo: string) => {
    if (open === appNo) { setOpen(null); return; }
    setOpen(appNo);
    const cur = rows[appNo];
    if (cur && cur.kind !== 'idle' && cur.kind !== 'error') return;
    setRow(appNo, { kind: 'loading' });
    try {
      const r = await pbrService.ipgodCandidate(cultivarId, appNo, false);
      if (r.success && r.data) setRow(appNo, { kind: 'review', data: r.data });
      else setRow(appNo, { kind: 'error', message: r.message || 'Could not read this record' });
    } catch (e) {
      setRow(appNo, { kind: 'error', message: errorMessage(e) });
    }
  };

  const add = async (appNo: string, data: IpgodCandidateReview) => {
    setRow(appNo, { kind: 'adding', data });
    try {
      const r = await pbrService.ipgodCandidate(cultivarId, appNo, true);
      if (r.success && r.data) {
        setRow(appNo, { kind: 'added', data: r.data });
        onApplied();
      } else {
        setRow(appNo, { kind: 'error', message: r.message || 'Could not add the protection', data });
      }
    } catch (e) {
      setRow(appNo, { kind: 'error', message: errorMessage(e), data });
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-amber-900">
            Close matches in the AU PBR registry
          </h3>
          <p className="mt-1 text-amber-800">
            None is named exactly &ldquo;{cultivarName}&rdquo;, so nothing was added. Review a record, and
            if it is this cultivar, add it as its Australian PBR protection.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ul className="mt-3 space-y-2">
        {candidates.map((c) => {
          const s = rows[c.application_number] ?? { kind: 'idle' };
          const data = 'data' in s ? s.data : undefined;
          const isOpen = open === c.application_number;
          return (
            <li key={c.application_number} className="rounded-md border border-amber-200 bg-white">
              <button
                type="button"
                onClick={() => review(c.application_number)}
                className="flex w-full items-center justify-between gap-3 p-3 text-left"
                aria-expanded={isOpen}
              >
                <span className="min-w-0">
                  <span className="block font-medium text-neutral-900 break-words">{c.denomination || 'No denomination recorded'}</span>
                  <span className="block text-xs text-neutral-500">Application {c.application_number}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2 text-primary-700">
                  {s.kind === 'added' && <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Added" />}
                  <span className="text-xs font-medium">{isOpen ? 'Hide' : 'Review'}</span>
                  {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </span>
              </button>

              {isOpen && (
                <div className="border-t border-amber-100 p-3">
                  {s.kind === 'loading' && (
                    <p className="flex items-center gap-2 text-neutral-600">
                      <Loader2 className="h-4 w-4 animate-spin" /> Reading the registry record…
                    </p>
                  )}

                  {data && (
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                      <div><dt className="text-xs text-neutral-500">Variety denomination</dt><dd className="break-words">{data.denomination || '—'}</dd></div>
                      <div><dt className="text-xs text-neutral-500">Applicant</dt><dd className="break-words">{data.applicant || 'Not given (individuals are not named)'}</dd></div>
                      <div><dt className="text-xs text-neutral-500">Species (UPOV taxon)</dt><dd className="break-words">{data.taxon || '—'}</dd></div>
                      <div><dt className="text-xs text-neutral-500">Registry status</dt><dd>{data.registry_status || '—'}</dd></div>
                      <div><dt className="text-xs text-neutral-500">Filed</dt><dd>{date(data.protection.application_date)}</dd></div>
                      <div><dt className="text-xs text-neutral-500">Granted</dt><dd>{date(data.protection.grant_date)}</dd></div>
                      {data.deemed_retired_date && (
                        <div><dt className="text-xs text-neutral-500">Deemed retired</dt><dd>{date(data.deemed_retired_date)}</dd></div>
                      )}
                      <div className="sm:col-span-2 text-xs text-neutral-500">
                        Adds an AU PBR protection, status <strong>{data.protection.status.replace(/_/g, ' ')}</strong>,
                        number {data.protection.registration_number}, from {data.dataset}.
                        {existingAuPbrNumber && existingAuPbrNumber !== data.protection.registration_number && (
                          <span className="mt-1 block text-amber-800">
                            A cultivar has one AU PBR protection. Its current one ({existingAuPbrNumber}) takes this
                            record&rsquo;s number, status and dates; anything this record lacks is kept.
                          </span>
                        )}
                      </div>
                    </dl>
                  )}

                  {s.kind === 'error' && (
                    <p className="mt-2 text-red-700">{s.message}</p>
                  )}

                  {s.kind === 'added' ? (
                    <p className="mt-3 flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> Added as this cultivar&rsquo;s AU PBR protection.
                    </p>
                  ) : data && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={s.kind === 'adding'}
                        onClick={() => add(c.application_number, data)}
                      >
                        {s.kind === 'adding'
                          ? <><Loader2 className="h-4 w-4 animate-spin" /> Adding…</>
                          : <><Plus className="h-4 w-4" /> This is it — add the protection</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setOpen(null)}>
                        Not this one
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
